import { ChangeEvent, SyntheticEvent, useMemo, useState } from 'react';
import {
  AutocompleteCloseReason,
  ClickAwayListener,
  InputAdornment,
  TextField,
} from '@mui/material';
import { Check, ChevronDown, Search } from 'lucide-react';
import COUNTRIES, { Country } from '@/constants/countries';

import StyledAutocomplete from '../StyledAutocomplete';

import { tokens } from '@/styles/tokens';

import * as S from './styles';

type SelectCountryAutocompleteProps = {
  isHighlighted?: boolean;
  value?: string;
  onChange?: (val: string) => void;
};

function sortCountries(countries: Array<Country>, selectedCountryCode: Country['code']) {
  // Sort the countries alphabetically by label
  const sortedCountries = countries.sort((a, b) => a.label.localeCompare(b.label));

  // Find the selected country
  const selectedCountryIndex = sortedCountries.findIndex(
    (country) => country.code === selectedCountryCode,
  );

  // Remove the selected country from the sorted list
  const [selectedCountry] = sortedCountries.splice(selectedCountryIndex, 1);

  // Insert the selected country at the beginning
  sortedCountries.unshift(selectedCountry);

  return sortedCountries;
}

export default function SelectCountryAutocomplete({
  isHighlighted,
  value,
  onChange,
}: SelectCountryAutocompleteProps) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const countryLabel = COUNTRIES.find((country) => country.code === value)?.label;
  const SORTED_COUNTRIES = useMemo(
    () => (value ? sortCountries(COUNTRIES, value) : COUNTRIES),
    [value],
  );

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    if (anchorEl) {
      anchorEl.focus();
    }
    setAnchorEl(null);
  };

  const handleChange = (event: SyntheticEvent, value: Country | null) => {
    if (value) {
      onChange?.(value.code);
    }
    handleClose();
  };

  const open = Boolean(anchorEl);
  const id = open ? 'country-autocomplete-popper' : undefined;

  return (
    <S.Container>
      <S.StyledButton
        fullWidth
        disableRipple
        variant='outlined'
        aria-describedby={id}
        onClick={handleClick}
        active={open}
        highlighted={isHighlighted}
        endIcon={
          <ChevronDown
            color={tokens.primary.darker}
            strokeWidth={1.5}
            opacity={0.4}
            aria-hidden='true'
          />
        }
      >
        {countryLabel && value ? (
          <S.CountryLabelWrapper>
            <img
              loading='lazy'
              width='20'
              src={`https://flagcdn.com/w20/${value.toLowerCase()}.png`}
              srcSet={`https://flagcdn.com/w40/${value.toLowerCase()}.png 2x`}
              className={'PhoneInputCountryIcon--border'}
              alt=''
            />
            {countryLabel}
          </S.CountryLabelWrapper>
        ) : (
          'Select country'
        )}
      </S.StyledButton>
      <S.StyledPopper
        disablePortal
        id={id}
        open={open}
        anchorEl={anchorEl}
        placement='bottom-start'
      >
        <ClickAwayListener onClickAway={handleClose}>
          <div>
            <StyledAutocomplete
              open
              disablePortal
              onChange={handleChange}
              popupIcon={false}
              onClose={(event: ChangeEvent<{}>, reason: AutocompleteCloseReason) => {
                if (reason === 'escape') {
                  handleClose();
                }
              }}
              options={SORTED_COUNTRIES}
              getOptionLabel={(option) => option.label}
              renderOption={(props, option) => (
                <li {...props}>
                  <img
                    loading='lazy'
                    width='20'
                    src={`https://flagcdn.com/w20/${option.code.toLowerCase()}.png`}
                    srcSet={`https://flagcdn.com/w40/${option.code.toLowerCase()}.png 2x`}
                    alt=''
                  />
                  <S.ItemLabel>
                    {option.label}
                    {option.code === value && <Check size={24} />}
                  </S.ItemLabel>
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  variant='outlined'
                  {...params}
                  placeholder='Search countries'
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <InputAdornment position='start'>
                        <Search />
                      </InputAdornment>
                    ),
                  }}
                />
              )}
            />
          </div>
        </ClickAwayListener>
      </S.StyledPopper>
    </S.Container>
  );
}
